import { useEffect, useRef } from "react";

/* Keeping what the browser filled in, against React's determination to undo it.

   The bug, exactly.

   These are controlled inputs: React owns `value` and, on every commit, writes
   its own state back onto the DOM node whenever the two disagree. iOS AutoFill
   sets `node.value` directly. When Safari's event doesn't reach React's
   delegated listener — which is routine when AutoFill fills several fields at
   once from a saved login — React's state stays `""`, and the very next render
   writes that empty string back over the address the person is looking at.

   Then we tell them to enter a valid email. They can see one. There is nothing
   they can type that satisfies it, so they tap again, and leave.

   It is worse than a single stale field. Any state change re-renders the whole
   form, so typing one character into the password box wipes the autofilled name
   AND email in the same commit. The clickstream shows the shape of it: two taps
   nine seconds apart, `value_length: 0` both times, on an iPhone.

   Reading the DOM at submit time — which the form already does — cannot fix
   this. By then React has overwritten the node. The value has to be captured at
   the moment it arrives, before the next commit.

   Three ways of noticing, because no one of them is reliable everywhere:

     1. Native `input`/`change` listeners bound to the node itself, in the
        capture phase. React listens at the root via delegation; a listener on
        the element runs for any event actually dispatched, so this catches
        fills that React's synthetic layer drops.
     2. `animationstart` from a keyframe attached to `:-webkit-autofill`. Chrome
        and Safari apply that pseudo-class the instant a field is autofilled,
        and it fires even when no input event does. This is the only signal for
        a silent fill.
     3. A short sweep over the first few seconds, as a backstop for anything the
        other two miss. Bounded on purpose — it is a safety net, not a poll.

   And one deterministic guarantee on top, which is what actually closes the
   hole. The clobber is not caused by the browser; it is caused by *us*, on the
   next commit after any setState. Those setStates come from our own field
   handlers. So `adoptNow()` sweeps every field synchronously, and the forms
   call it at the top of each onChange and before reading values to submit — so
   a fill is captured in the same tick as the keystroke that would have wiped
   it, with no race to lose. The listeners and sweeps above still matter for
   fills that are never followed by any typing at all.

   Every path does the same thing: if the node holds something React doesn't
   know about, adopt it into state. Adoption is one-way and never clears — we
   only ever take a non-empty DOM value that differs from state, so this can
   never fight the user for control of a field they are typing in. */

let keyframesInstalled = false;

/* The autofill pseudo-class carries no event of its own, so we hang a
   zero-effect animation off it and listen for that. Named distinctively
   because we key on animationName to ignore every other animation on the page. */
const ANIMATION_NAME = "sift-autofill-start";

function installKeyframes() {
  if (keyframesInstalled || typeof document === "undefined") return;
  keyframesInstalled = true;
  try {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes ${ANIMATION_NAME} { from { opacity: 1; } to { opacity: 1; } }
      input:-webkit-autofill { animation-name: ${ANIMATION_NAME}; animation-duration: 1ms; }
    `;
    document.head.appendChild(style);
  } catch {
    /* CSP or a detached document: the other two signals still work */
  }
}

/**
 * Adopt browser-filled values that React never heard about.
 *
 * @param refs    the ref map from useFieldErrors (`refs.current[field] = node`)
 * @param setters { fieldName: (value) => void } — usually the field's setState
 * @param deps    re-bind when the fields on screen change (e.g. a stage change)
 *
 * Setters are read through a ref-free closure each sweep, so passing fresh
 * arrow functions on every render is fine and does not re-bind listeners.
 */
export function useAutofillAdoption(refs, setters, deps = []) {
  /* Held in a ref so `adoptNow` is stable across renders and always sweeps
     using the current setters. Without this, a form calling adoptNow() from an
     onChange would close over the first render's setters forever. */
  const sweepRef = useRef(() => {});

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    installKeyframes();

    const names = Object.keys(setters);
    let disposed = false;

    // Last value we handed to React, per field. Lets us tell "the DOM has
    // something new" from "we already adopted this and React hasn't committed
    // yet" — without it, a slow commit would make us re-adopt every sweep.
    const adopted = {};

    const adopt = (field) => {
      if (disposed) return;
      const node = refs.current?.[field];
      const setter = setters[field];
      if (!node || !setter) return;
      const domValue = typeof node.value === "string" ? node.value : "";
      // Never clear, never fight the user: only ever take a non-empty value
      // that we have not already passed along.
      if (!domValue || adopted[field] === domValue) return;
      adopted[field] = domValue;
      setter(domValue);
    };

    const adoptAll = () => names.forEach(adopt);
    sweepRef.current = adoptAll;

    // 1. Native listeners on the nodes themselves.
    const bound = [];
    names.forEach((field) => {
      const node = refs.current?.[field];
      if (!node) return;
      const handler = () => adopt(field);
      node.addEventListener("input", handler, true);
      node.addEventListener("change", handler, true);
      bound.push([node, handler]);
    });

    // 2. The autofill pseudo-class, which is the only signal for a silent fill.
    const onAnimationStart = (e) => {
      if (e.animationName !== ANIMATION_NAME) return;
      const field = names.find((f) => refs.current?.[f] === e.target);
      if (field) adopt(field);
      else adoptAll();
    };
    document.addEventListener("animationstart", onAnimationStart, true);

    /* 3. The backstop. Front-loaded and finite: autofill happens within a
          moment of the field appearing or of the AutoFill bar being tapped, so
          a handful of checks over three seconds covers it without leaving a
          timer running on a phone. */
    const timers = [80, 250, 600, 1200, 2000, 3000].map((ms) => setTimeout(adoptAll, ms));

    // Returning from the password-manager sheet is a fill we would otherwise
    // only notice on the next sweep.
    window.addEventListener("focus", adoptAll);
    document.addEventListener("visibilitychange", adoptAll);

    return () => {
      disposed = true;
      bound.forEach(([node, handler]) => {
        node.removeEventListener("input", handler, true);
        node.removeEventListener("change", handler, true);
      });
      document.removeEventListener("animationstart", onAnimationStart, true);
      timers.forEach(clearTimeout);
      window.removeEventListener("focus", adoptAll);
      document.removeEventListener("visibilitychange", adoptAll);
      sweepRef.current = () => {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  /* Sweep every field right now, synchronously.

     Call this immediately before anything that sets state — a field's onChange,
     or reading the form to submit it. React will write its own state back over
     the DOM on the commit that follows; this takes what the browser put there
     first, in the same tick, so there is no window to lose.

     Stable identity, safe to call from a handler on every keystroke: it is a
     string compare per field and adopts nothing when nothing has changed. */
  return useRef((...args) => sweepRef.current(...args)).current;
}
